// ErrorCard (HANDOFF §5.10 / board system/error--retry-and-409): a card IN the
// content area, never a modal; the shell keeps working around it.
//   network → «لم تنجح المحاولة.» + «تحقّق من الاتصال ثم أعد المحاولة. ما حُرّر على هذا الجهاز محفوظ محلياً.»
//             + «أعد المحاولة» + «العمل بلا اتصال»
//   409     → «لا يمكن تنفيذ هذا الإجراء.» + the cause + «حدّث الشاشة» — never an
//             automatic retry of the same write.

import type { ReactNode } from 'react';
import { CircleAlert, CloudOff, RefreshCw } from 'lucide-react';
import { isApiError } from '@/lib/api';
import { Button } from './Button';
import { BrandMark } from './BrandMark';

export type ErrorKind = 'network' | 'conflict';

export const ERROR_COPY = {
  networkTitle: 'لم تنجح المحاولة.',
  networkBody: 'تحقّق من الاتصال ثم أعد المحاولة. ما حُرّر على هذا الجهاز محفوظ محلياً.',
  /** The same body without the local-save clause (screens that queue nothing — decision 12). */
  networkBodyNoLocal: 'تحقّق من الاتصال ثم أعد المحاولة.',
  retry: 'أعد المحاولة',
  workOffline: 'العمل بلا اتصال',
  conflictTitle: 'لا يمكن تنفيذ هذا الإجراء.',
  conflictBodyFallback: 'تغيّر على جهاز آخر قبل حفظك (409). حدّث الشاشة ثم أعد ما تريد؛ لا تُعاد المحاولة نفسها تلقائياً.',
  refresh: 'حدّث الشاشة',
} as const;

/** 409 → 'conflict'; everything else that reaches an ErrorCard reads as 'network'. */
export function errorKindOf(error: unknown): ErrorKind {
  return isApiError(error) && error.status === 409 ? 'conflict' : 'network';
}

export interface ErrorCardProps {
  error?: unknown;
  /** Force a kind (otherwise derived from `error`). */
  kind?: ErrorKind;
  /** 409 cause sentence (default: the problem's Arabic `detail`). */
  cause?: ReactNode;
  /** Prefix for the 409 cause, e.g. «سند القبض #0059». */
  subject?: ReactNode;
  onRetry?: () => void;
  /** Shown only where offline work is allowed (invoices, receipt vouchers). */
  onWorkOffline?: () => void;
  onRefresh?: () => void;
  /**
   * The network body promises «ما حُرّر على هذا الجهاز محفوظ محلياً.» — true
   * only where the local queue exists (invoices, receipt vouchers). false drops
   * the clause (appointments, customers, catalogue…). Default true.
   */
  localSave?: boolean;
}

export function ErrorCard({ error, kind, cause, subject, onRetry, onWorkOffline, onRefresh, localSave = true }: ErrorCardProps) {
  const k = kind ?? errorKindOf(error);
  if (k === 'conflict') {
    const detail = cause ?? (isApiError(error) && error.detail ? error.detail : ERROR_COPY.conflictBodyFallback);
    return (
      <section className="e-errcard" data-kind="conflict" role="alert">
        <span className="e-errcard-icon" aria-hidden="true">
          <CircleAlert />
        </span>
        <div className="e-errcard-head">
          <BrandMark mode="failed" size={24} />
          <h2 className="e-errcard-title">{ERROR_COPY.conflictTitle}</h2>
        </div>
        <p className="e-errcard-body">
          {subject && <>{subject} </>}
          {detail}
        </p>
        {onRefresh && (
          <div className="e-errcard-actions">
            <Button variant="teal-line" icon={RefreshCw} onClick={onRefresh}>
              {ERROR_COPY.refresh}
            </Button>
          </div>
        )}
      </section>
    );
  }
  return (
    <section className="e-errcard" data-kind="network" role="alert">
      <span className="e-errcard-icon" aria-hidden="true">
        <CloudOff />
      </span>
      <div className="e-errcard-head">
        <BrandMark mode="failed" size={24} />
        <h2 className="e-errcard-title">{ERROR_COPY.networkTitle}</h2>
      </div>
      <p className="e-errcard-body">{localSave ? ERROR_COPY.networkBody : ERROR_COPY.networkBodyNoLocal}</p>
      <div className="e-errcard-actions">
        {onRetry && (
          <Button variant="teal-line" icon={RefreshCw} onClick={onRetry}>
            {ERROR_COPY.retry}
          </Button>
        )}
        {onWorkOffline && (
          <Button variant="ghost" onClick={onWorkOffline}>
            {ERROR_COPY.workOffline}
          </Button>
        )}
      </div>
    </section>
  );
}
